import { useCallback } from 'react';
import { generateStableListKey, generateStableKey, addPendingChange } from '../utils/helpers';
import { get, postJson, putJson, del as apiDelete } from '../utils/apiClient';

/**
 * Hook for list CRUD operations
 */
export default function useListOperations({ setCache, setSelectedListId, isOnline, showNotification }) {

  // Create a new list
  const handleCreateList = useCallback(async (e) => {
    e.preventDefault();
    const newListName = e.target.elements.newListName.value;
    if (!newListName) return;

    const tempId = `temp-${Date.now()}`;
    const now = new Date();
    const tempList = {
      id: tempId,
      tempId,
      name: newListName,
      created_at: now,
      position: Date.now(), // Highest position → sorts to top
      stableKey: generateStableListKey({ id: tempId, tempId })
    };

    console.log(`📝 [Create List] Creating temp list "${newListName}" with ID: ${tempId}`);

    // Optimistic update
    setCache(c => ({ ...c, lists: [tempList, ...c.lists] }));
    e.target.reset();

    try {
      // Create the list on the server
      const { data: createdList } = await postJson(`/lists`, { name: newListName });
      console.log(`✅ [Create List] Successfully created list "${newListName}" on server with ID: ${createdList.id}`);

      // Generate default prompts based on list name
      try {
        console.log(`🤖 [Create List] Generating default prompts for "${newListName}"...`);
        const { data: promptsData } = await postJson(`/ai/generate-prompts`, { listName: newListName });

        if (promptsData.prompts && promptsData.prompts.length > 0) {
          console.log(`✅ [Create List] Generated ${promptsData.prompts.length} prompts:`, promptsData.prompts);

          // Update the list with the generated prompts
          await putJson(`/lists/${createdList.id}/custom-prompts`, {
            custom_prompts: promptsData.prompts
          });

          console.log(`✅ [Create List] Set default prompts for list "${newListName}"`);
        }
      } catch (promptError) {
        // Don't fail list creation if prompt generation fails
        console.warn('⚠️ [Create List] Failed to generate default prompts, continuing without them:', promptError);
      }

      // Firebase listener will handle adding the real list with prompts
    } catch (error) {
      console.error('Create list failed', error);
      if (!error.response) {
        // Network error: keep temp list visible, mark as pending sync, queue
        setCache(c => {
          const updatedLists = c.lists.map(l =>
            l.id === tempId ? { ...l, isPendingSync: true } : l
          );
          return addPendingChange(
            { ...c, lists: updatedLists },
            { type: 'createList', payload: { name: newListName } }
          );
        });
        if (showNotification) {
          showNotification('List saved offline - will sync when connected', 'info');
        }
      } else {
        // Server error: revert optimistic update
        setCache(c => ({ ...c, lists: c.lists.filter(l => l.id !== tempId) }));
      }
    }
  }, [setCache, showNotification]);

  // Delete a list
  const handleDeleteList = useCallback(async (listId) => {
    // Optimistic update
    setCache((c) => ({ ...c, lists: c.lists.filter((l) => l.id !== listId) }));

    try {
      await apiDelete(`/lists/${listId}`);
    } catch (error) {
      // Treat 404 as success (idempotent delete)
      if (error?.response?.status === 404) return;
      console.error('Delete list failed', error);
      // Let real-time listeners reconcile state if needed
    }
  }, [setCache]);

  // Update list visibility settings
  const handleUpdateListVisibility = useCallback(async (listId, visibility, public_editable, allow_link_sharing) => {
    try {
      const updateData = {
        visibility,
        public_editable
      };

      if (allow_link_sharing !== undefined) {
        updateData.allow_link_sharing = allow_link_sharing;
      }

      await putJson(`/lists/${listId}/visibility`, updateData);

      // Update the list in cache optimistically
      setCache(c => ({
        ...c,
        lists: c.lists.map(list =>
          list.id === listId
            ? { ...list, visibility, public_editable, allow_link_sharing }
            : list
        )
      }));
    } catch (error) {
      console.error('🌐 [Visibility] Failed to update list visibility:', error);
      throw error;
    }
  }, [setCache]);

  // Update custom prompts for a list
  const handleUpdateCustomPrompts = useCallback(async (listId, customPrompts) => {
    try {
      const { data: updatedList } = await putJson(`/lists/${listId}/custom-prompts`, {
        custom_prompts: customPrompts
      });

      // Update the list in cache
      setCache(c => ({
        ...c,
        lists: c.lists.map(list =>
          list.id === listId ? { ...list, ...updatedList } : list
        )
      }));
    } catch (error) {
      console.error('Update custom prompts failed', error);
      throw error;
    }
  }, [setCache]);

  // Process items with custom prompts
  const handleProcessItemsWithPrompts = useCallback(async (listId) => {
    if (!isOnline) {
      if (showNotification) {
        showNotification('Processing items requires an internet connection', 'warning');
      }
      return;
    }
    try {
      const { data: result } = await postJson(`/lists/${listId}/process-items`, {});

      // Refresh the items for this list
      const { data: updatedItems } = await get(`/lists/${listId}/items`);

      const itemsWithKeys = updatedItems.map(item => ({
        ...item,
        stableKey: generateStableKey(item)
      }));

      setCache(c => ({
        ...c,
        items: {
          ...c.items,
          [listId]: itemsWithKeys
        }
      }));

      alert(`Successfully processed ${result.updatedItems.length} items with custom prompts!`);
    } catch (error) {
      console.error('Process items with prompts failed', error);
      throw error;
    }
  }, [setCache, isOnline, showNotification]);

  // Persist a user-driven reorder of the dashboard lists. We hand back the
  // already-reordered array so the caller can update local state immediately
  // (drag UX); we then assign descending `position` values and PUT each
  // changed list to the server.
  const handleReorderLists = useCallback(async (orderedLists) => {
    const baseTime = Date.now();
    // Spacing between positions so future inserts have room without rewriting.
    const STEP = 1000;
    const updated = orderedLists.map((list, idx) => ({
      ...list,
      position: baseTime - idx * STEP,
    }));

    setCache(c => {
      // Preserve any lists that weren't part of the reorder set (shouldn't
      // happen in practice but be safe).
      const idsBeingReordered = new Set(updated.map(l => l.id));
      const others = (c.lists || []).filter(l => !idsBeingReordered.has(l.id));
      return { ...c, lists: [...updated, ...others] };
    });

    if (!isOnline) return;
    // Skip temp lists — they don't exist on the server yet.
    const serverWrites = updated
      .filter(l => !String(l.id).startsWith('temp-'))
      .map(l => putJson(`/lists/${l.id}`, { position: l.position }).catch(err => {
        console.error('Reorder list failed', l.id, err);
      }));
    await Promise.all(serverWrites);
  }, [setCache, isOnline]);

  return {
    handleCreateList,
    handleDeleteList,
    handleUpdateListVisibility,
    handleUpdateCustomPrompts,
    handleProcessItemsWithPrompts,
    handleReorderLists,
  };
}
